import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, OnDestroy, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Task } from '../../shared/entities/task/task';
import { WidgetCoordinatorService } from '../../../core/services/issue/widget-coordinator.service';
import { EditDialogService } from '../../../core/components/editDialog/editDialogService';
import { CommandInput } from '../../../core/entities/commandTemplate/commandTemplate';
import { CommandService, Reloadable } from '../../../core/services/command/command.service';
import { RefetchCoordinator } from '../../../core/services/refetch-coordinator/refetch-coordinator.service';
import { ToastService } from '../../../core/services/toast/toast.service';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { AddressDialogComponent } from 'src/app/shared-module/components/address-dialog/address-dialog.component';
import { environment } from 'src/environments/environment';
import { MenuItem } from 'primeng/api';

@Component({
  selector: 'nx-update-task-address-widget',
  templateUrl: './update-task-address-widget.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UpdateTaskAddressWidgetComponent implements Reloadable, OnDestroy {

  private dialogRef: DynamicDialogRef | undefined;

  // Computed signal for menu items based on task/address state
  readonly menuItems = computed<MenuItem[]>(() => {
    const address = this.task()?.address;
    const gradeStr = address?.geocodingResultGrade ? String(address.geocodingResultGrade).toUpperCase() : '';
    
    return [
      {
        label: 'Kopieren',
        icon: 'pi pi-copy',
        disabled: gradeStr !== 'YELLOW' && gradeStr !== 'GREEN',
        command: () => this.copyAddress()
      },
      {
        label: 'In Google Maps öffnen',
        icon: 'pi pi-map-marker',
        disabled: gradeStr !== 'YELLOW' && gradeStr !== 'GREEN',
        command: () => this.openInGoogleMaps()
      },
      {
        label: 'Akzeptieren',
        disabled: gradeStr !== 'YELLOW',
        icon: 'pi pi-check',
        command: () => this.acceptAddress()
      }
    ];
  });

  // Injected services
  private readonly commandService = inject(CommandService);
  private readonly coordinator = inject(WidgetCoordinatorService);
  private readonly refetchCoordinator = inject(RefetchCoordinator);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogService = inject(EditDialogService);
  private readonly toastService = inject(ToastService);

  // Signal inputs
  readonly task = input.required<Task>();

  // Computed signal to check if geocoding grade is green or yellow
  // GraphQL returns the enum as a string ("GREEN", "YELLOW", "RED")
  readonly shouldShowAddressImage = computed(() => {
    const grade = this.task()?.address?.geocodingResultGrade;
    if (!grade) return false;
    
    // Handle both string and enum values
    const gradeStr = String(grade).toUpperCase();
    return gradeStr === 'GREEN' || gradeStr === 'YELLOW';
  });

  // Computed signal for static map image URL
  readonly staticMapUrl = computed(() => {
    const address = this.task()?.address;
    if (!address?.lat || !address?.lng) return null;
    
    const apiKey = environment.generalHereApiKey;
    const lat = address.lat;
    const lng = address.lng;
    
    // Map dimensions
    const width = 250;
    const height = 120;
    const zoom = 16;
    
    // Map style
    const style = 'explore.day';
    
    // Marker configuration
    const markerColor = '%23DAFF00'; // Yellow-green color (URL encoded #DAFF00)
    const markerSize = 20;
    const markerOutlineColor = '%23000000'; // Black outline (URL encoded #000000)
    
    // Build HERE Maps v3 API URL
    const baseUrl = 'https://image.maps.hereapi.com/mia/v3/base/mc';
    const mapCenter = `center:${lat},${lng}`;
    const mapZoom = `zoom=${zoom}`;
    const mapSize = `${width}x${height}`;
    const format = 'png';
    
    const queryParams = [
      `style=${style}`,
      `apiKey=${apiKey}`,
      `overlay=point:${lat},${lng};color=${markerColor};size=${markerSize};outline-color=${markerOutlineColor}`
    ].join('&');
    
    return `${baseUrl}/${mapCenter};${mapZoom}/${mapSize}/${format}?${queryParams}`;
  });

  // State signals
  readonly isLoading = signal(false);

  // Widget ID for coordinator
  private readonly widgetId = `assign-task-widget-${Math.random().toString(36).substr(2, 9)}`;
  private unregisterRefetch?: () => void;

  // For Reloadable interface
  readonly reloadable: Reloadable = this;

  constructor() {
    // Subscribe to cancel all requests
    this.coordinator.cancelAll$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(exceptId => {
        if (exceptId !== this.widgetId) {
          // Close dialog if open
          this.dialogRef?.close();
        }
      });

    // Effect to handle task changes and register with RefetchCoordinator
    effect(() => {
      const currentTask = this.task();
      if (currentTask?.id) {
        // Register with RefetchCoordinator in untracked to avoid circular dependencies
        untracked(() => {
          this.unregisterRefetch?.();
          this.unregisterRefetch = this.refetchCoordinator.register(
            this,
            currentTask.getEntityType(),
            currentTask.id
          );
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.unregisterRefetch?.();
  }

  edit() {
    this.coordinator.requestCancelAll(this.widgetId);
    
    const currentTask = this.task();
    if (!currentTask?.id) return;

    this.dialogRef = this.dialogService.open(AddressDialogComponent, {
      width: "1100px",
      header: 'Adresse bearbeiten',
      data: {
        address: currentTask.address,
      }
    });
    
    this.dialogRef.onClose.subscribe((result) => {
      if (result && result.addressInput) {
        this.isLoading.set(true);
        
        const commandInput = new CommandInput();
        commandInput.name = 'taskUpdateAddressCommand';
        commandInput.addParameter('commandProviderId', currentTask.id);
        commandInput.addParameter("addressInput", result.addressInput);
        
        this.commandService.sendCommand(commandInput, () => {
          this.isLoading.set(false);
          this.refetchCoordinator.emit({ 
            entityType: currentTask.getEntityType(), 
            entityId: currentTask.id
          });
        });
      }
    });
  }

  reload(): void {
    // Reload is handled by RefetchCoordinator
    // No need to reload command template for this dialog-based widget
  }

  copyAddress(): void {
    const address = this.task()?.address;
    if (!address) return;

    const addressString = address.getAddressString();
    if (addressString) {
      navigator.clipboard.writeText(addressString).then(() => {
        this.toastService.addToastWithMessage('Adresse wurde kopiert.');
      }).catch(err => {
        console.error('Failed to copy address:', err);
      });
    }
  }
  openInGoogleMaps(): void {
    const address = this.task()?.address;
    if (!address) return;

    let url = '';
    const typeStr = String(address.type).toUpperCase();
    
    if (typeStr === 'COORDINATES_ONLY' && address.lat && address.lng) {
      // Use coordinates only if type is COORDINATES_ONLY
      url = `https://www.google.com/maps/search/?api=1&query=${address.lat},${address.lng}`;
    } else {
      // Use address string for all other types
      const addressString = address.getAddressString();
      if (addressString) {
        url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressString)}`;
      }
    }

    if (url) {
      window.open(url, '_blank');
    }
  }

  acceptAddress(): void {
    const currentTask = this.task();
    const address = currentTask?.address;
    if (!address || !currentTask?.id) return;

    this.isLoading.set(true);
    
    const commandInput = new CommandInput();
    commandInput.name = 'rentalResourceAssignmentTaskAcceptAddressCommand';
    commandInput.addParameter('commandProviderId', currentTask.id);
    
    this.commandService.sendCommand(commandInput, () => {
      this.isLoading.set(false);
      this.refetchCoordinator.emit({ 
        entityType: currentTask.getEntityType(), 
        entityId: currentTask.id
      });
      this.toastService.addToastWithMessage('Adresse akzeptiert.');
    });
  }
}
